import { useEffect, useMemo, useRef, useState } from 'react';
import { createEventClient, generateSessionId, type EventClient } from '@aicores/events';
import type { PortalContent } from './types';
import { PortalHeader } from './PortalHeader';
import { PortalFooter } from './PortalFooter';
import { Hero } from './Hero';
import { StartHereBlock } from './StartHereBlock';
import { ModuleSection } from './ModuleSection';
import { PromptCard } from './PromptCard';
import { BonusSection } from './BonusSection';
import { ProgressTracker } from './ProgressTracker';
import { ClosingQuestion } from './ClosingQuestion';
import { NextUpBlock } from './NextUpBlock';
import { SequenceToast } from './SequenceToast';
import { LoadingState, ErrorState } from './states';
import { useToken } from './useToken';

const FIRST_VISIT_KEY = (productId: string) => `aicores:${productId}:first_visit_at`;
const VISIT_COUNT_KEY = (productId: string) => `aicores:${productId}:visit_count`;

interface Props {
  content: PortalContent;
  entitlementEndpoint: string;
  eventsEndpoint: string;
  debugEvents?: boolean;
}

export function Portal({ content, entitlementEndpoint, eventsEndpoint, debugEvents }: Props) {
  const token = useToken({ endpoint: entitlementEndpoint, productId: content.product_id });

  if (token.status === 'loading') {
    return <LoadingState />;
  }
  if (token.status === 'invalid' || !token.token) {
    return <ErrorState reason={token.reason} />;
  }
  return (
    <PortalContents
      content={content}
      token={token.token}
      firstName={token.user?.first_name ?? null}
      eventsEndpoint={eventsEndpoint}
      debugEvents={debugEvents}
    />
  );
}

function PortalContents({
  content,
  token,
  firstName,
  eventsEndpoint,
  debugEvents,
}: {
  content: PortalContent;
  token: string;
  firstName: string | null;
  eventsEndpoint: string;
  debugEvents?: boolean;
}) {
  const sessionId = useRef(generateSessionId());
  const events: EventClient = useMemo(
    () =>
      createEventClient({
        endpoint: eventsEndpoint,
        token,
        productId: content.product_id,
        sessionId: sessionId.current,
        debug: debugEvents,
      }),
    [token, content.product_id, eventsEndpoint, debugEvents],
  );

  const [copiedPrompts, setCopiedPrompts] = useState<Set<string>>(new Set());
  const [viewedPrompts, setViewedPrompts] = useState<Set<string>>(new Set());
  const [viewedBonuses, setViewedBonuses] = useState<Set<string>>(new Set());
  const [completedAssessments, setCompletedAssessments] = useState<Set<string>>(new Set());
  const [toastVisible, setToastVisible] = useState(false);
  const toastShownThisSession = useRef(false);
  const foundationEngaged = useRef(false);
  const openedAt = useRef(Date.now());

  // portal_opened + return_visit
  useEffect(() => {
    const key = FIRST_VISIT_KEY(content.product_id);
    const visitKey = VISIT_COUNT_KEY(content.product_id);
    const firstVisitRaw = window.localStorage.getItem(key);
    const firstVisit = firstVisitRaw ? Number(firstVisitRaw) : null;
    const now = Date.now();
    const isFirstVisit = !firstVisit;
    if (isFirstVisit) {
      window.localStorage.setItem(key, String(now));
      window.localStorage.setItem(visitKey, '1');
    } else {
      const visitCount = Number(window.localStorage.getItem(visitKey) ?? '1') + 1;
      window.localStorage.setItem(visitKey, String(visitCount));
      const hoursSince = (now - firstVisit) / (1000 * 60 * 60);
      if (hoursSince >= 24) {
        events.fire('return_visit', {
          days_since_first_visit: Math.floor(hoursSince / 24),
          visit_number: visitCount,
        });
      }
    }
    events.fire('portal_opened', {
      referrer: document.referrer || '',
      is_first_visit: isFirstVisit,
      token_age_hours: null,
    });
  }, [content.product_id, events]);

  // portal_exited
  useEffect(() => {
    function onBeforeUnload() {
      events.fire('portal_exited', {
        duration_seconds: Math.round((Date.now() - openedAt.current) / 1000),
        prompts_viewed_count: viewedPrompts.size,
        prompts_copied_count: copiedPrompts.size,
      });
      void events.flush();
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [events, copiedPrompts.size, viewedPrompts.size]);

  function markCopied(id: string) {
    setCopiedPrompts((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }

  function markViewed(id: string) {
    setViewedPrompts((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    if (
      content.foundation_module_id &&
      content.modules?.find((m) => m.module_id === content.foundation_module_id)?.prompt_ids
        .includes(id)
    ) {
      foundationEngaged.current = true;
    }
  }

  function markBonusViewed(id: string) {
    setViewedBonuses((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }

  function markAssessmentCompleted(id: string) {
    setCompletedAssessments((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }

  function reopenPriorityFinder() {
    const bonus = content.bonuses?.find((b) => b.feature === 'priority_finder');
    if (!bonus) return;
    const el = document.getElementById(`bonus-${bonus.bonus_id}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.setTimeout(() => {
      const trigger = el.querySelector<HTMLButtonElement>('.bonus-card-trigger');
      trigger?.click();
    }, 400);
  }

  const priorityFinderBonus = content.bonuses?.find((b) => b.feature === 'priority_finder');
  const hasFoundation = content.modules?.some((m) => m.is_foundation) ?? false;
  const showNextUp = Boolean(priorityFinderBonus) || hasFoundation;

  function handleRequiresFoundationExpand(promptId: string) {
    if (foundationEngaged.current) return;
    // Fire skip event + show toast once per session.
    const attempted = content.prompts.find((p) => p.prompt_id === promptId);
    if (!attempted?.module_id) return;
    events.fire('sequence_skip_detected', {
      attempted_module: attempted.module_id,
      attempted_prompt_id: promptId,
    });
    if (!toastShownThisSession.current) {
      toastShownThisSession.current = true;
      setToastVisible(true);
    }
  }

  const closingVisible = Boolean(
    content.closing_question?.enabled &&
      (content.closing_question.trigger === 'after_first_prompt_copied'
        ? copiedPrompts.size >= 1
        : copiedPrompts.size >= 5),
  );

  const promptsByModule = useMemo(() => {
    const map = new Map<string, typeof content.prompts>();
    for (const prompt of content.prompts) {
      const key = prompt.module_id ?? '__none__';
      const list = map.get(key) ?? [];
      list.push(prompt);
      map.set(key, list);
    }
    return map;
  }, [content.prompts]);

  return (
    <div className="portal">
      <PortalHeader firstName={firstName} />
      <Hero hero={content.hero} />
      {content.has_modules && content.modules ? (
        <ProgressTracker modules={content.modules} copiedPromptIds={copiedPrompts} />
      ) : null}
      <StartHereBlock
        copy={content.start_here}
        events={events}
        productId={content.product_id}
        copiedPromptIds={copiedPrompts}
        viewedBonusIds={viewedBonuses}
        completedAssessmentIds={completedAssessments}
        modules={content.modules}
      />
      {showNextUp ? (
        <NextUpBlock
          content={content}
          copiedPromptIds={copiedPrompts}
          bonusId={priorityFinderBonus?.bonus_id ?? null}
          onViewFullResults={priorityFinderBonus ? reopenPriorityFinder : undefined}
        />
      ) : null}
      <main className="main-content">
        <div className="section-heading">
          {content.has_modules && content.modules
            ? `The ${content.prompts.length} prompts`
            : `${content.prompts.length} prompts`}
        </div>

        {content.has_modules && content.modules
          ? content.modules.map((module) => {
              const mprompts = promptsByModule.get(module.module_id) ?? [];
              return (
                <ModuleSection
                  key={module.module_id}
                  module={module}
                  events={events}
                  isSecondary={!module.is_foundation && content.sequence_enforcement === 'soft_emphasis'}
                >
                  {mprompts.map((prompt) => (
                    <PromptCard
                      key={prompt.prompt_id}
                      prompt={prompt}
                      events={events}
                      onCopied={markCopied}
                      onViewed={markViewed}
                      defaultExpanded={
                        module.default_expanded && prompt === mprompts[0] ? true : false
                      }
                      requiresFoundation={
                        content.sequence_enforcement === 'soft_emphasis' && !module.is_foundation
                      }
                      onRequiresFoundationExpand={handleRequiresFoundationExpand}
                    />
                  ))}
                </ModuleSection>
              );
            })
          : (
            <div className="prompts">
              {content.prompts.map((prompt) => (
                <PromptCard
                  key={prompt.prompt_id}
                  prompt={prompt}
                  events={events}
                  onCopied={markCopied}
                  onViewed={markViewed}
                />
              ))}
            </div>
          )}

        {content.bonuses && content.bonuses.length > 0 ? (
          <BonusSection
            bonuses={content.bonuses}
            events={events}
            eyebrow={content.bonuses_heading?.eyebrow}
            title={content.bonuses_heading?.title}
            productId={content.product_id}
            content={content}
            copiedPromptIds={copiedPrompts}
            onBonusViewed={markBonusViewed}
            onAssessmentCompleted={markAssessmentCompleted}
          />
        ) : null}

        {content.closing_question ? (
          <ClosingQuestion
            config={content.closing_question}
            events={events}
            visible={closingVisible}
          />
        ) : null}
      </main>
      <PortalFooter />
      <SequenceToast visible={toastVisible} onClose={() => setToastVisible(false)} />
    </div>
  );
}
